/**
 * keepAlive
 *
 * Sends a best-effort GET request to the keep-alive endpoint so the server
 * extends the user's session.  The response body is discarded immediately —
 * only the status code matters.
 *
 * @param {string}      url     - The keep-alive endpoint URL.
 * @param {AbortSignal} signal  - An AbortSignal to cancel the request if a
 *                                newer keep-alive supersedes this one.
 * @returns {Promise<void>}
 */
export default function keepAlive(url, signal) {
    return fetch(url, {
        method: 'GET',
        // Marked so server-side code can distinguish this from a full-page
        // navigation, keeping parity with the previous XHR implementation.
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal,
    })
        .then((response) => {
            // Discard the body stream immediately — we only care about the
            // status code.  This releases the connection back to the pool
            // without waiting for the full body to download and decode.
            if (response.body) {
                response.body.cancel();
            }
        })
        .catch((err) => {
            // AbortError is expected when the caller cancels an in-flight
            // request (e.g. the user clicks "Stay signed in" a second time
            // before the first ping completes).  All other network errors are
            // also swallowed — the keep-alive ping is best-effort and should
            // never surface to the user.
            if (err.name !== 'AbortError') {
                // Intentionally silent — see comment above.
            }
        });
}
